<template>
  <div class="contract-review-content">
    <div class="contract-review-content-header">
      <div class="contract-review-content-header-title">合同审查</div>
      <div class="contract-review-content-header-desc">
        上传合同文件，AI
        将进行风险识别、条款分析、修改建议生成，并产出审查意见书与修订版合同。
      </div>
    </div>
    <div class="contract-review-content-upload">
      <div>
        <div class="contract-review-content-upload-title">
          <span class="title-icon">
            <!-- <SvgIcon name="bill" :size="25" /> -->
            <SafetyOutlined :style="{ fontSize: '24px', color: '#ffffff' }" />
          </span>
          上传合同，开始审查
        </div>
        <div class="contract-review-content-upload-desc">
          支持合同原文、扫描版合同和历史版本。上传后系统将先解析合同双方、合同类型和条款结构，再进入审查配置。
        </div>
      </div>
      <div class="contract-review-content-upload-box" ref="uploadBoxRef">
        <!-- 重新上传用的隐藏文件输入：直接唤起选择框，不切换 UI -->
        <input
          ref="hiddenInputRef"
          type="file"
          accept=".doc,.docx,.pdf"
          class="hidden-file-input"
          @change="onHiddenInputChange"
        />
        <a-upload-dragger
          v-if="!uploadedFile || uploading"
          v-model:fileList="fileList"
          name="file"
          :multiple="true"
          :custom-request="customRequest"
          :disabled="uploading"
          :show-upload-list="false"
          accept=".doc,.docx,.pdf"
        >
          <div v-if="uploading" class="upload-progress-container">
            <div class="upload-progress-header">
              <LoadingOutlined class="upload-progress-spin" />
              <span class="upload-progress-title">正在上传，请稍候…</span>
            </div>
            <div class="upload-progress-list">
              <div
                v-for="item in uploadingFiles"
                :key="item.uid"
                class="upload-progress-item"
              >
                <div class="upload-progress-item-info">
                  <span class="upload-progress-item-name" :title="item.name">{{
                    item.name
                  }}</span>
                  <span class="upload-progress-item-percent"
                    >{{ item.percent }}%</span
                  >
                </div>
                <div class="upload-progress-bar">
                  <div
                    class="upload-progress-bar-inner"
                    :style="{ width: item.percent + '%' }"
                  ></div>
                </div>
              </div>
            </div>
          </div>
          <template v-else>
            <p class="ant-upload-drag-icon">
              <span class="upload-icon-bg">
                <UploadOutlined :style="{ fontSize: '32px' }" />
              </span>
            </p>
            <p class="ant-upload-text">点击或拖拽合同到此处上传</p>
            <p class="ant-upload-hint">
              文件将进行加密传输与安全解析,支持DOC/DOCX/PDF&nbsp;,&nbsp;单文件≤50MB
            </p>
            <div class="upload-buttons">
              <a-button type="primary">
                <span class="btn-inner">
                  <SvgIcon name="folder-close" :size="20" />
                  选择合同文件
                </span>
              </a-button>
              <a-button
                type="link"
                class="sample-link-btn"
                @click.stop="handleUseSample"
              >
                使用样例审查
              </a-button>
            </div>
          </template>
        </a-upload-dragger>
        <div v-else class="uploaded-state">
          <div class="file-card">
            <div class="file-ico" :class="`file-ico-${uploadedFile.iconKind}`">
              {{ uploadedFile.iconLetter }}
            </div>
            <div class="file-main">
              <div class="file-name" :title="uploadedFile.name">
                {{ uploadedFile.name }}
              </div>
              <div class="file-meta">{{ uploadedFile.meta }}</div>
            </div>
            <a-button
              type="text"
              class="icon-btn"
              title="删除"
              @click="handleRemoveUploaded"
            >
              <template #icon><DeleteOutlined /></template>
            </a-button>
          </div>
          <div class="parse-row">
            <span class="parse-dot"><CheckOutlined /></span>
            <span>文件解析完成，已识别双方主体、合同类型和关键条款</span>
          </div>
          <div class="upload-actions">
            <a-button class="btn-reupload" @click="handleReupload"
              >重新上传</a-button
            >
            <a-button type="primary" class="btn-next" @click="handleNextStep">
              下一步：配置审查要求
            </a-button>
          </div>
        </div>
      </div>
    </div>
    <div class="contract-review-content-recently">
      <div class="recently-title">
        <span>最近审查</span>
        <button class="recently-view-all" @click="handleViewAll">
          全部<RightOutlined />
        </button>
      </div>
      <div class="recently-list">
        <a-empty v-if="recentReviewList.length === 0" />
        <div
          v-for="item in recentReviewList"
          :key="item.id"
          class="recently-list-item"
          @click="handleOpenRecent(item)"
        >
          <div class="recently-list-item-main">
            <div class="recently-list-item-name">
              {{ item.name
              }}<span v-if="item.isDraft" class="recently-list-item-draft"
                >草稿合同</span
              >
            </div>
            <div class="recently-list-item-info">
              {{ item.time }}
            </div>
          </div>
          <span
            class="recently-list-item-status"
            :class="`recently-status-${item.statusClass}`"
          >
            {{ item.statusLabel }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import {
  UploadOutlined,
  LoadingOutlined,
  DeleteOutlined,
  CheckOutlined,
  RightOutlined,
  SafetyOutlined,
} from "@ant-design/icons-vue";
import { message } from "ant-design-vue";
import { uploadFile, getRecentHistory } from "@/api/contractReview";

const router = useRouter();

const recentReviewList = ref([]);

// 拉取最近审查列表
// 后端字段名尚未最终确定，这里对常用命名做兜底，接口确定后可统一
const normalizeRecentItem = (raw) => {
  const status = raw.status ?? raw.reviewStatus ?? raw.Status;
  const isCompleted = status === "Completed";
  return {
    id: raw.id ?? raw.contractId ?? raw.reviewId ?? raw.recordId,
    fileId: raw.fileId ?? raw.file_id,
    revisedFileId: raw.revisedFileId ?? raw.revised_file_id,
    name:
      raw.name ?? raw.contractName ?? raw.fileName ?? raw.title ?? "未命名合同",
    // 草稿合同标记：true 显示"草稿合同"标签（后端可能返回字符串布尔，双兜底）
    isDraft: raw.isDraft === true || raw.isDraft === "true",
    time: raw.time ?? raw.reviewTime ?? raw.createTime ?? raw.createdAt,
    count: raw.count ?? raw.opinionCount ?? raw.opinionsCount ?? 0,
    status,
    statusLabel: isCompleted ? "审查完成" : "待审查",
    statusClass: isCompleted ? "completed" : "pending",
  };
};

const fetchRecentReviewList = async () => {
  try {
    const res = await getRecentHistory(3);
    // 后端返回结构可能是 { data: [...] } 或直接数组，两种都兼容
    const list = Array.isArray(res) ? res : res?.data || [];
    recentReviewList.value = list.map(normalizeRecentItem);
  } catch (err) {
    console.error("[ContractReview] getRecentHistory error:", err);
  }
};

onMounted(() => {
  fetchRecentReviewList();
});

// 上传文件列表
const fileList = ref([]);
// 正在上传的文件列表：[{ uid, name, percent }]
const uploadingFiles = ref([]);
const uploading = computed(() => uploadingFiles.value.length > 0);

// 上传完成后展示的合同信息
const uploadedFile = ref(null);
const uploadBoxRef = ref(null);
// 重新上传用的隐藏 input
const hiddenInputRef = ref(null);

const formatFileSize = (size) => {
  if (!size) return "0 MB";
  const mb = size / (1024 * 1024);
  if (mb >= 1) return mb.toFixed(1) + " MB";
  return Math.max(1, Math.round(size / 1024)) + " KB";
};

const getFileExt = (name) => {
  const parts = name.split(".");
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
};

const getFileIcon = (name) => {
  const ext = getFileExt(name);
  if (ext === "doc" || ext === "docx") return { letter: "W", kind: "word" };
  if (ext === "pdf") return { letter: "P", kind: "pdf" };
  return { letter: "F", kind: "file" };
};

// 上传接口 type 入参：1=doc/docx，2=pdf
const getFileType = (name) => (getFileExt(name) === "pdf" ? 2 : 1);

// 根据本地文件和后端响应拼装 meta 信息
// 后端字段名尚未确定，这里做了多种命名兜底，等接口确定后可统一
const buildUploadedMeta = (file, res) => {
  const parts = [formatFileSize(file.size)];
  const data = (res && (res.data || res)) || {};
  const wordCount = data.wordCount || data.word_count || data.charCount;
  if (wordCount) parts.push(`${wordCount.toLocaleString()} 字`);
  const contractType =
    data.contractType || data.contract_type || data.contractKind;
  if (contractType) parts.push(contractType);
  return parts.join("｜");
};

// 单文件大小上限：50MB
const MAX_FILE_SIZE = 50 * 1024 * 1024;

// 自定义上传：调用后端 /api/ContractReview/UploadContract
const customRequest = async (options) => {
  const { file, onSuccess, onError } = options;
  // 文件格式校验：仅支持 DOC/DOCX/PDF，其他格式直接拦截，不进入上传队列
  // accept 只过滤文件选择框，拖拽仍可绕过，必须在此处兜底校验
  const ext = getFileExt(file.name);
  if (ext !== "doc" && ext !== "docx" && ext !== "pdf") {
    message.error(`${file.name} 格式不支持，仅支持 DOC/DOCX/PDF 格式`);
    onError(new Error("文件格式不支持"));
    return;
  }
  // 文件大小校验：超过 50MB 直接拦截，不进入上传队列
  if (file.size > MAX_FILE_SIZE) {
    message.error(`${file.name} 超过 50MB，无法上传`);
    onError(new Error("文件超过 50MB 限制"));
    return;
  }
  const uid = file.uid || file.name;
  uploadingFiles.value.push({ uid, name: file.name, percent: 0 });
  try {
    const res = await uploadFile(file, getFileType(file.name), (percent) => {
      // 上传完成（100%）后还要等服务端响应，先把可视进度封顶在 98，
      // 避免「100% 卡很久」的错觉
      const item = uploadingFiles.value.find((f) => f.uid === uid);
      if (item) item.percent = Math.min(percent, 98);
    });
    // 服务端响应已回，标记到 100%
    const item = uploadingFiles.value.find((f) => f.uid === uid);
    if (item) item.percent = 100;
    onSuccess(res, file);
    message.success(`${file.name} 上传成功`);

    const icon = getFileIcon(file.name);
    // contractId 本地保留：下一步跳配置页通过 query 携带（数据统一走 GetDetailById）
    const data = (res && (res.data || res)) || {};
    uploadedFile.value = {
      name: file.name,
      size: file.size,
      contractId: data.contractId || data.id || data.contract_id,
      meta: buildUploadedMeta(file, res),
      iconLetter: icon.letter,
      iconKind: icon.kind,
      rawRes: res,
    };

    console.log("[ContractReview] upload success:", res);
  } catch (err) {
    onError(err);
    message.error(`${file.name} 上传失败`);
    console.error("[ContractReview] upload error:", err);
  } finally {
    // 稍作停留再移除，避免进度条到 100% 后瞬间消失
    setTimeout(() => {
      uploadingFiles.value = uploadingFiles.value.filter((f) => f.uid !== uid);
    }, 400);
  }
};

// 删除已上传文件，回到上传初始态
const handleRemoveUploaded = () => {
  uploadedFile.value = null;
  fileList.value = [];
};

// 重新上传：保持当前 UI 不变，直接唤起文件选择
const handleReupload = () => {
  // 重置 value，确保连续选择同一个文件也能触发 change
  if (hiddenInputRef.value) hiddenInputRef.value.value = "";
  hiddenInputRef.value?.click();
};

// 样例审查：public 下的静态 docx（BASE_URL 为 publicPath，encodeURI 处理中文文件名）
const SAMPLE_CONTRACT_URL = encodeURI(
  `${process.env.BASE_URL}设备采购合同.docx`,
);
const SAMPLE_CONTRACT_NAME = "设备采购合同.docx";

// 使用样例审查：不打开选择框，直接拉取样例文件走与手动上传完全一致的流程
// （进度条、格式/大小校验、成功后的解析卡片与共享状态回填都复用 customRequest）
// sampleLoading 防抖：fetch 样例文件期间（上传 UI 还没切换）手速快连点会重复上传；
// uploading 兜底上传进行中的窗口期。finally 复位保证删除文件后可再次使用样例
let sampleLoading = false;
const handleUseSample = async () => {
  if (sampleLoading || uploading.value) return;
  sampleLoading = true;
  try {
    const resp = await fetch(SAMPLE_CONTRACT_URL);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    const file = new File([blob], SAMPLE_CONTRACT_NAME, {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });
    // 替换之前的文件（与重新上传路径保持一致）
    uploadedFile.value = null;
    fileList.value = [];
    customRequest({
      file,
      onSuccess: () => {},
      onError: () => {},
    });
  } catch (err) {
    console.error("[ContractReview] load sample error:", err);
    message.error("样例文件加载失败，请确认 public 下已放置 设备采购合同.docx");
  } finally {
    sampleLoading = false;
  }
};

// 隐藏 input 的 change 处理：复用 dragger 的 customRequest 走完整上传流程
// - 之前的文件默认被替换（清空 uploadedFile / 共享状态）
// - customRequest 会写入 uploadingFiles，dragger 会显示与首次一致的进度条 UI
const onHiddenInputChange = (e) => {
  const file = e.target.files?.[0];
  // 重置 value，确保连续选择同一个文件也能触发 change
  if (e.target) e.target.value = "";
  if (!file) return;
  // 替换之前的文件
  uploadedFile.value = null;
  fileList.value = [];
  // 走与首次上传一致的逻辑：onSuccess / onError 仅作为回调占位，副作用都在 customRequest 内
  customRequest({
    file,
    onSuccess: () => {},
    onError: () => {},
  });
};

// 下一步：跳转到审查要求配置页，id 带上传返回的 contractId
// （配置页统一走 getGetDetailById 拉数据，不再读共享状态）
const handleNextStep = () => {
  const id = uploadedFile.value?.contractId;
  if (!id) {
    message.warning("请先上传合同");
    return;
  }
  router.push({ path: "/home/contract-review/requirements", query: { id } });
};

// 点击最近审查项：跳转到审查结果页，并把 id 通过 query 带过去
const handleOpenRecent = (item) => {
  if (!item?.id) return;
  const query = { id: item.id, fileId: item.revisedFileId || item.fileId };
  if (item.status === "Completed") {
    router.push({
      path: "/home/contract-review/result",
      query: { ...query, status: item.status },
    });
  } else {
    router.push({ path: "/home/contract-review/requirements", query });
  }
};

// 查看全部：跳转到审查列表页
const handleViewAll = () => {
  router.push("/home/contract-review/list");
};

// 下载样例合同：阻止冒泡到 a-upload-dragger，避免触发文件选择框
// const handleDownloadSample = () => {
//   // TODO: 替换为实际的样例合同下载地址
//   window.open("https://example.com/sample-contract.docx", "_blank");
// };
</script>

<style lang="less" scoped>
.contract-review-content {
  line-height: 1.5;
  margin: 24px 0px 0px;
  text-shadow: 0px 6px 18px rgba(31, 45, 61, 0.05);
  display: grid;
  grid-template-columns: minmax(0, 1fr) 380px;
  gap: 24px;
  align-items: start;
  .contract-review-content-header {
    grid-column: 1 / -1;
  }
  .contract-review-content-header-title {
    font-size: 34px;
    font-weight: 600;
  }
  .contract-review-content-header-desc {
    font-size: 15px;
    color: #64748f;
  }
  .contract-review-content-upload {
    margin: 0;
    padding: 24px;
    background: #ffffff;
    border-radius: 16px 16px 16px 16px;
    border: 1px solid #e6ebf2;
    .contract-review-content-upload-title {
      color: #1f2d3d;
      font-size: 24px;
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;

      .title-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 48px;
        height: 48px;
        background: #1768ff;
        color: #ffffff;
        box-shadow: 0px 6px 18px 0px rgba(31, 45, 61, 0.05),
          0px 6px 18px 0px rgba(31, 45, 61, 0.05);
        border-radius: 14px;
        margin-right: 0px;
      }
    }
    .contract-review-content-upload-desc {
      font-size: 16px;
      color: #5e6e89;
    }
    .contract-review-content-upload-box {
      margin-top: 24px;
      height: 360px;
      display: flex;
      align-items: stretch;
      justify-content: center;
      .ant-upload-text {
        font-weight: 600;
        font-size: 20px;
        color: #3f455b;
      }
      .ant-upload-hint {
        font-size: 16px;
        color: #61728b;
      }
      .upload-buttons {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 12px;
        margin-top: 16px;
        .ant-btn {
          width: 216px;
          height: 44px;
          font-size: 16px;
        }
        // 样例审查 link 按钮 hover：浅蓝底 + 文字加深（与最近审查卡片 hover 同色调）
        .sample-link-btn {
          border-radius: 8px;
          transition: background 0.15s ease, color 0.15s ease;
          &:hover {
            background: #f1f6fe;
            color: #0d5cd9;
          }
        }
        .btn-inner {
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
      }
    }
  }
  .contract-review-content-recently {
    background-color: #ffffff;
    padding: 24px;
    box-shadow: 0px 6px 18px 0px rgba(31, 45, 61, 0.05);
    border-radius: 16px 16px 16px 16px;
    border: 1px solid #e6ebf2;
    .recently-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-weight: 600;
      font-size: 16px;
      color: #1f2d3d;
      .recently-view-all {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        border: none;
        background: transparent;
        color: #1768ff;
        font-size: 14px;
        font-weight: 400;
        line-height: 1;
        padding: 0;
        cursor: pointer;
        &:hover {
          color: #0d5cd9;
        }
      }
    }
    .recently-list-item {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      background: #fafbfd;
      padding: 16px;
      box-shadow: 0px 6px 18px 0px rgba(31, 45, 61, 0.05);
      border-radius: 12px 12px 12px 12px;
      border: 1px solid #edf1f7;
      margin-top: 16px;
      cursor: pointer;
      transition: border-color 0.2s ease, box-shadow 0.2s ease,
        background 0.2s ease;
      &:hover {
        background: #f1f6fe;
        border-color: #c7d6ea;
        box-shadow: 0px 6px 18px 0px rgba(23, 104, 255, 0.1);
      }
      .recently-list-item-name {
        font-weight: 600;
        font-size: 14px;
        color: #223550;
        .recently-list-item-draft {
          display: inline-block;
          margin-left: 8px;
          vertical-align: 1px;
          font-size: 12px;
          font-weight: 400;
          line-height: 1;
          padding: 4px 8px;
          border-radius: 8px;
          color: #64748f;
          background: #eef1f6;
        }
      }
      .recently-list-item-info {
        font-size: 13px;
        color: #465872;
      }
      .recently-list-item-main {
        flex: 1;
        min-width: 0;
      }
      .recently-list-item-status {
        flex-shrink: 0;
        margin-left: 8px;
        font-size: 12px;
        line-height: 1;
        padding: 4px 10px;
        border-radius: 999px;
        &.recently-status-completed {
          color: #167b59;
          background: #e6f7f0;
        }
        &.recently-status-pending {
          color: #b76e00;
          background: #fff7e6;
        }
      }
    }
  }
}
:deep(.ant-upload-wrapper) {
  width: 100%;
  height: 100%;
}
:deep(.ant-upload-wrapper .ant-upload-drag) {
  background-color: #f7fafe;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
}
:deep(.ant-upload-wrapper .ant-upload-drag .ant-upload) {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.upload-progress-container {
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
  padding: 8px 24px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}
.upload-progress-header {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #1768ff;
  font-size: 16px;
  font-weight: 600;
}
.upload-progress-spin {
  font-size: 18px;
  animation: cr-upload-spin 1s linear infinite;
}
@keyframes cr-upload-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
.upload-progress-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.upload-progress-item {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.upload-progress-item-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #3f455b;
}
.upload-progress-item-name {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.upload-progress-item-percent {
  color: #1768ff;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}
.upload-progress-bar {
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: #e6ebf2;
  overflow: hidden;
}
.upload-progress-bar-inner {
  height: 100%;
  border-radius: 999px;
  background: #1768ff;
  transition: width 0.2s ease;
}
.uploaded-state {
  width: 100%;
  height: 100%;
  padding: 24px;
  background: #f7fafe;
  border: 1px dashed #c7d6ea;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: 16px;
}
.uploaded-state .file-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 6px 18px rgba(31, 45, 61, 0.05);
}
.uploaded-state .file-ico {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #1768ff;
  color: #ffffff;
  font-weight: 700;
  font-size: 18px;
  flex-shrink: 0;
}
.uploaded-state .file-ico-pdf {
  background: #e5484d;
}
.uploaded-state .file-main {
  flex: 1;
  min-width: 0;
}
.uploaded-state .file-name {
  font-size: 15px;
  font-weight: 600;
  color: #1f2d3d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.uploaded-state .file-meta {
  font-size: 13px;
  color: #64748f;
  margin-top: 4px;
}
.uploaded-state .icon-btn {
  flex-shrink: 0;
  color: #94a3b8;
}
.uploaded-state .icon-btn:hover {
  color: #ef4444;
}
.uploaded-state .parse-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #167b59;
}
.uploaded-state .parse-dot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #167b59;
  color: #ffffff;
  font-size: 11px;
  flex-shrink: 0;
}
.uploaded-state .upload-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
}
.uploaded-state .upload-actions .ant-btn {
  height: 40px;
  padding: 0 20px;
  font-size: 14px;
  border-radius: 8px;
}
.upload-icon-bg {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 78px;
  height: 78px;
  background: #ffffff;
  border-radius: 50%;
  box-shadow: 0px 6px 18px 0px rgba(31, 45, 61, 0.05),
    0px 6px 18px 0px rgba(31, 45, 61, 0.05);
  border: 1px solid #edeff5;
}
.hidden-file-input {
  display: none;
}
</style>
